import React from 'react';
import { makeStyles } from '@fluentui/react-components';
import { AlyzeOptimizedImage, createAlyzeOptimizedImageUrl } from '../../../utilities/AlyzeOptimizedImage';

const useStyles = makeStyles({
  row: {
    width: '100%',
    maxWidth: '1368px',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '172px',
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      gap: '40px',
    },
    '@media (max-width: 768px)': {
      gap: '32px',
    },
  },
  rowReverse: {
    justifyContent: 'flex-end',
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
    },
  },
  textContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: '20px',
    '@media (max-width: 1024px)': {
      alignItems: 'center',
      textAlign: 'center',
    },
  },
  title: {
    alignSelf: 'stretch',
    color: '#222222',
    fontSize: '40px',
    fontFamily: 'Figtree',
    fontWeight: '400',
    lineHeight: '56px',
    wordWrap: 'break-word',
    '@media (max-width: 768px)': {
      fontSize: '32px',
      lineHeight: '44.80px',
    },
  },
  description: {
    width: '401px',
    color: '#222222',
    fontSize: '18px',
    fontFamily: 'Figtree',
    fontWeight: '400',
    lineHeight: '25.20px',
    wordWrap: 'break-word',
    '@media (max-width: 1024px)': {
      width: '100%',
      maxWidth: '401px',
    },
  },
  image: {
    width: '668px',
    height: '540px',
    borderRadius: '40px',
    objectFit: 'cover',
    display: 'block',
    '@media (max-width: 1024px)': {
      width: '100%',
      height: 'auto',
      maxWidth: '668px',
    },
    '@media (max-width: 768px)': {
      borderRadius: '24px',
    },
  },
  imageContainer: {
    width: '668px',
    height: '540px',
    borderRadius: '40px',
    overflow: 'hidden',
    display: 'block',
    '@media (max-width: 1024px)': {
      width: '100%',
      height: 'auto',
      maxWidth: '668px',
    },
    '@media (max-width: 768px)': {
      borderRadius: '24px',
    },
  },
});

export interface AlyzeFeatureRowCardProps {
  title: string;
  description: React.ReactNode;
  imageSrc: string;
  imageAlt: string;
  imagePosition?: 'left' | 'right';
  imageObjectPosition?: string;
}

export const AlyzeFeatureRowCard: React.FC<AlyzeFeatureRowCardProps> = ({
  title,
  description,
  imageSrc,
  imageAlt,
  imagePosition = 'right',
  imageObjectPosition,
}) => {
  const styles = useStyles();

  const textContent = (
    <div className={styles.textContent}>
      <div className={styles.title}>{title}</div>
      <div className={styles.description}>{description}</div>
    </div>
  );

  const image = imageObjectPosition ? (
    <div 
      className={styles.imageContainer}
      style={{
        backgroundImage: `url(${createAlyzeOptimizedImageUrl({ 
          key: imageSrc,
          width: 1336,
          height: 1080,
          format: 'webp',
          quality: 80
        })})`,
        backgroundSize: '200%',
        backgroundPosition: imageObjectPosition,
        backgroundRepeat: 'no-repeat',
      }} 
      role="img"
      aria-label={imageAlt}
    />
  ) : (
    <AlyzeOptimizedImage 
      src={imageSrc} 
      alt={imageAlt} 
      className={styles.image}
    />
  );

  return (
    <div className={`${styles.row} ${imagePosition === 'right' ? styles.rowReverse : ''}`}>
      {imagePosition === 'left' ? (
        <>
          {image}
          {textContent}
        </>
      ) : (
        <>
          {textContent}
          {image}
        </>
      )}
    </div>
  );
};
